import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Sale } from '@/api/saleApi';
import { formatCurrency, formatDate } from '@/utils/helpers';
import SaleStatusBadge from './SaleStatusBadge';

interface SaleHistoryTableProps {
  sales: Sale[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  isLoading: boolean;
  isInitialLoading: boolean;
  error: string;
  isOwnScope: boolean;
  canCancel: boolean;
  pendingSaleId: number | null;
  onPageChange: (page: number) => void;
  onView: (sale: Sale) => void;
  onCancel: (sale: Sale) => void;
  onRetry: () => void;
}

const safeFormatDate = (value: string): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return formatDate(value);
};

const SaleHistoryTable: React.FC<SaleHistoryTableProps> = ({
  sales,
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  isLoading,
  isInitialLoading,
  error,
  isOwnScope,
  canCancel,
  pendingSaleId,
  onPageChange,
  onView,
  onCancel,
  onRetry,
}) => {
  const { t } = useTranslation();
  const firstItem = totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(currentPage * pageSize, totalElements);

  return (
    <div className="relative flex min-h-[430px] flex-1 flex-col">
      {isLoading && !isInitialLoading && (
        <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary-fixed" role="status" aria-label={t('admin.sales_history.loading')}>
          <div className="h-full w-1/2 animate-pulse bg-primary" />
        </div>
      )}

      <div className="flex-1 overflow-x-auto">
        <table className={`w-full min-w-[1120px] border-collapse text-left text-body-sm transition-opacity ${isLoading && !isInitialLoading ? 'opacity-60' : ''}`}>
          <thead className="bg-surface-container-low">
            <tr className="border-b border-outline-variant">
              {['code', 'customer', 'employee', 'total', 'payment', 'status', 'created_at', 'actions'].map((column) => (
                <th key={column} className={`px-4 py-3 font-label-sm text-label-sm text-secondary ${column === 'actions' ? 'text-right' : ''}`}>
                  {t(`admin.sales_history.table.${column}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/60 bg-white">
            {isInitialLoading && Array.from({ length: 7 }, (_, index) => (
              <tr key={index}>
                <td colSpan={8} className="px-4 py-3">
                  <div className="h-12 animate-pulse rounded-xl bg-surface-container-low" />
                </td>
              </tr>
            ))}

            {!isInitialLoading && error && sales.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.sales_history.error.title')}</p>
                  <p className="mt-1 text-error">{error}</p>
                  <button type="button" className="mt-4 rounded-xl bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>
                    {t('admin.sales_history.actions.retry')}
                  </button>
                </td>
              </tr>
            )}

            {!isInitialLoading && !error && sales.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-16 text-center text-secondary">
                  <span className="material-symbols-outlined text-4xl text-outline">receipt_long</span>
                  <p className="mt-2 font-medium text-on-surface">{t(`admin.sales_history.empty.${isOwnScope ? 'own_title' : 'title'}`)}</p>
                  <p className="mt-1">{t('admin.sales_history.empty.description')}</p>
                </td>
              </tr>
            )}

            {!isInitialLoading && sales.map((sale) => {
              const customerName = sale.customer?.name?.trim() || t('admin.sales_history.walk_in_customer');
              const customerPhone = sale.customer?.phone?.trim();
              const paymentMethods = Array.from(new Set(sale.payments.map((payment) => payment.method)));
              const paymentStatuses = Array.from(new Set(sale.payments.map((payment) => payment.status)));
              const isPending = pendingSaleId === sale.id;
              const showCancel = canCancel && sale.status === 'PAID';

              return (
                <tr key={sale.id} className={`cursor-pointer hover:bg-surface-container-lowest ${isPending ? 'opacity-60' : ''}`} onClick={() => onView(sale)}>
                  <td className="px-4 py-3">
                    <p className="whitespace-nowrap font-mono font-bold text-primary">{sale.code}</p>
                    <p className="mt-0.5 text-[12px] text-secondary">#{sale.id}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="max-w-[200px] truncate font-medium text-on-surface">{customerName}</p>
                    {customerPhone && <p className="mt-0.5 text-[12px] text-secondary">{customerPhone}</p>}
                  </td>
                  <td className="px-4 py-3 text-on-surface">{sale.createdBy.username}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono font-bold text-on-surface">{formatCurrency(sale.totalAmount)}</td>
                  <td className="px-4 py-3">
                    {sale.payments.length > 0 ? (
                      <div className="flex max-w-[180px] flex-wrap gap-1">
                        {paymentMethods.map((method) => <span key={method} className="rounded-full bg-primary-fixed px-2 py-0.5 font-label-sm text-primary">{method}</span>)}
                        {paymentStatuses.map((status) => <span key={status} className="rounded-full bg-surface-container px-2 py-0.5 font-label-sm text-secondary">{t(`admin.sales_history.payment_status.${status.toLowerCase()}`)}</span>)}
                      </div>
                    ) : <span className="text-secondary">—</span>}
                  </td>
                  <td className="px-4 py-3"><SaleStatusBadge status={sale.status} /></td>
                  <td className="whitespace-nowrap px-4 py-3 text-secondary">{safeFormatDate(sale.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-primary-fixed hover:text-primary disabled:opacity-50" disabled={isPending} aria-label={t('admin.sales_history.actions.view_label', { code: sale.code })} onClick={(event) => { event.stopPropagation(); onView(sale); }}>
                        <span className="material-symbols-outlined text-[19px]">visibility</span>
                      </button>
                      {showCancel && (
                        <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-error hover:bg-error-container/50 disabled:opacity-50" disabled={isPending} aria-label={t('admin.sales_history.actions.cancel_label', { code: sale.code })} onClick={(event) => { event.stopPropagation(); onCancel(sale); }}>
                          <span className="material-symbols-outlined text-[19px]">cancel</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-col items-center justify-between gap-3 border-t border-outline-variant bg-white px-4 py-3 text-center sm:flex-row sm:text-left">
        <p className="text-body-sm text-secondary">{t('admin.sales_history.pagination.summary', { first: firstItem, last: lastItem, total: totalElements })}</p>
        <div className="flex items-center gap-2">
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40" disabled={currentPage <= 1 || isLoading} aria-label={t('admin.sales_history.pagination.previous')} onClick={() => onPageChange(currentPage - 1)}>
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="min-w-24 text-center font-label-md text-on-surface">{t('admin.sales_history.pagination.page', { current: currentPage, total: Math.max(totalPages, 1) })}</span>
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40" disabled={currentPage >= totalPages || isLoading} aria-label={t('admin.sales_history.pagination.next')} onClick={() => onPageChange(currentPage + 1)}>
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SaleHistoryTable;
